/* eslint-disable no-case-declarations */
import { AfterViewInit, Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import {
    AUDIT_PAYLOAD,
    AUDIT_RESPONSE,
    AUDIT_RESPONSE_KEYS,
} from '../../interfaces/audit.interface';
import { FilterPipe } from 'ngx-filter-pipe';
import {
    CommonService,
    Destroy,
    FIELD_TYPE,
    SelectFormFields,
    SnackService,
    ToastService,
} from '@increff/commons';
import { AuditService } from '../../services/audit/audit.service';
import { OrganisationService } from '../../services/organisation/organisation.service';
import { ApplicationsService } from '../../services/applications/applications.service';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { APPLICATION } from '../../interfaces/application.interface';
import { ORGANISATION } from '../../interfaces/organisation.interface';
import { DatePipe } from '@angular/common';
import { ConfigService } from '../../services';

export interface FORM_OBJ {
    [key: string]: string | string[];
}

@Component({
    selector: 'com-audit-log',
    templateUrl: './audit-log.component.html',
    styleUrls: ['./audit-log.component.scss'],
    viewProviders: [Destroy, DatePipe],
})
export class AuditLogComponent implements OnInit, AfterViewInit {

    @ViewChild('crudActionsTemplateRef', { static: true })
    private crudActionsTemplateRef: TemplateRef<any>;

    public flow: 'admin' | 'domain' = 'admin';
    public appName: string;
    public domainId: number;
    public selectedAction: string;
    public today: Date;
    public searchText: string;
    filterColumns: Array<string> = [];
    public auditList: AUDIT_RESPONSE[] = [];
    public searchResults: AUDIT_RESPONSE[] = [];
    public auditActions: string[] = [];
    public applications: APPLICATION[] = [];
    public organisations: ORGANISATION[] = [];
    public paginationParams: { pageNo: number; pageSize: number };

    applicationSelect: SelectFormFields = {
        key: 'applicationSelect',
        label: 'Application',
        type: FIELD_TYPE.CUSTOM_SELECT,
        required: true,
        disabled: !this.applications?.length,
        options: []
    };

    actionSelect: SelectFormFields = {
        key: 'action',
        label: 'Action',
        type: FIELD_TYPE.CUSTOM_SELECT,
        required: false,
        disabled: !this.auditActions?.length,
        options: []
    };

    applicationDomainSelect: SelectFormFields = {
        key: 'organisationSelect',
        label: 'Organisation',
        type: FIELD_TYPE.CUSTOM_SELECT,
        required: true,
        disabled: !this.organisations?.length,
        options: []
    };

    private searchSubject: Subject<string> = new Subject<string>();
    private DEBOUNCE_TIME: number = 400;

    constructor(
        private auditService: AuditService,
        private organisationsService: OrganisationService,
        private applicationService: ApplicationsService,
        private commonService: CommonService,
        private toast: ToastService,
        private snack: SnackService,
        private activatedRoute: ActivatedRoute,
        private datePipe: DatePipe,
        private filterPipe: FilterPipe,
        private readonly destroy$: Destroy,
        private configService: ConfigService
    ) {
        this.today = new Date();
        this.snack.wait();
        this.flow = this.activatedRoute?.snapshot?.data?.flow ?? 'admin';
        this.getAuditActions();
        if(this.flow === 'admin') this.getApplications();
        const obj: AUDIT_RESPONSE_KEYS = {
            domain: undefined,
            appName: undefined,
            domainName: undefined,
            action: undefined,
            operationTime: undefined,
            actor: undefined,
            subject: undefined,
            description: undefined,
        };
        this.filterColumns = [...Object.keys(obj)];
    }

    ngOnInit(): void {
        this.searchSubject
            .pipe(debounceTime(this.DEBOUNCE_TIME), takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    this.searchData(response);
                },
            });
    }

    ngAfterViewInit(): void {
        this.configService.pageHeaderExtraTemplateRef =
            this.crudActionsTemplateRef;
        this.initTooltip();
    }

    resetForm(form: NgForm): void{
        form?.reset({action: ''});
        this.appName = this.domainId = this.selectedAction = undefined;
        this.applicationSelect.value = '';
        this.applicationSelect = {...this.applicationSelect};
        this.applicationDomainSelect.value = -1;
        this.applicationDomainSelect.options = [];
        this.applicationDomainSelect = {...this.applicationDomainSelect};
        this.actionSelect.value = '';
        this.actionSelect = {...this.actionSelect};
    }

    getApplications(): void {
        this.applicationService
            .getAllApplications()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    this.applications = response ?? [];
                    this.setApplicationSelectOptions();
                },
            });
    }

    setApplicationSelectOptions(): void {
        this.applicationSelect.options = this.applications?.map((appObj)=> ({displayKey: `${appObj?.fullName} (${appObj?.appName})`, valueKey: appObj?.appName}));
        this.applicationSelect.disabled = !this.applications?.length;
        this.applicationSelect.value = this.appName;
        this.applicationSelect = {...this.applicationSelect};
    }

    getOrganisations(appName: string): void {
        this.toast?.clear();
        this.organisationsService
            .getApplicationDomains(appName)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    this.organisations = response ?? [];
                    this.setApplicationDomainSelectOptions();
                },
            });
    }

    setApplicationDomainSelectOptions(): void {
        this.applicationDomainSelect.options = this.organisations?.map((orgObj)=> ({displayKey: orgObj?.domainName, valueKey: orgObj?.id}));
        this.applicationDomainSelect.disabled = !this.organisations?.length;
        this.applicationDomainSelect.value = this.domainId;
        this.applicationDomainSelect = {...this.applicationDomainSelect};
    }

    areFiltersValid(form: NgForm, flow: 'admin' | 'domain' = 'admin'): boolean {
        this.toast?.clear();
        switch (flow) {
        case 'admin':
            if(!this.appName){
                this.toast?.warn('Application is required!');
                return false;
            }
            if(!this.domainId) {
                this.toast?.warn('Organisation is required!');
                return false;
            }
            if(form?.invalid){
                this.toast?.warn('Start Date and End Date are required!');
                return false;
            }
            return true;
        default:
            if(form?.invalid){
                this.toast?.warn('Start Date and End Date are required!');
                return;
            }
            return true;
        }
    }

    searchAuditLog(form?: NgForm): void {
        if(!this.areFiltersValid(form, this.flow)) return;

        this.auditService
            .getAuditLog(
                this.getFormattedPayload((form?.value ?? {}) as FORM_OBJ),
                this.flow
            )
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    this.auditList = response ?? [];
                    this.snack?.success();
                    this.initTooltip();
                },
            });
    }

    downloadAuditReport(form: NgForm): void {
        if(!this.areFiltersValid(form, this.flow)) return;
        this.auditService.getAuditReport(this.getFormattedPayload((form?.value ?? {}) as FORM_OBJ), this.flow)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response)=> {
                    const downloadLink = document.createElement('a');
                    const blobData = new Blob([response], {
                        type: 'application/octet-stream',
                    });
                    downloadLink.href = window.URL.createObjectURL(blobData);
                    downloadLink.setAttribute(
                        'download',
                        `${this.flow}-audit-report.csv`
                    );
                    document.body.appendChild(downloadLink);
                    downloadLink.click();
                    downloadLink.parentNode.removeChild(downloadLink);
                },
            });
    }

    getFormattedPayload(valueObj: FORM_OBJ): AUDIT_PAYLOAD {
        const resultObj = {};
        if (valueObj?.['startDate'])
            resultObj['startTime'] = new Date(valueObj['startDate'] as string).toISOString();
        if (valueObj?.['endDate'])
            resultObj['endTime'] = new Date( valueObj['endDate'] as string).toISOString();
        if (this.flow === 'admin') resultObj['domainId'] = this.domainId;
        if (this.selectedAction) resultObj['action'] = this.selectedAction;

        return resultObj as AUDIT_PAYLOAD;
    }

    checkLimit(startValueSpy, endValueSpy): void | boolean {
        this.toast.clear();
        const startDateNum = Number(
            new Date(startValueSpy.value as string)
        );
        const endDateNum = Number(
            new Date(endValueSpy.value as string)
        );
        const todayNum = Number(new Date());
        switch (true) {
        case startDateNum > todayNum:
            startValueSpy.reset();
            this.toast.warn(
                `Start Date cannot be selected greater than today's date!`
            );
            return;
        case endDateNum > todayNum:
            endValueSpy.reset();
            this.toast.warn(
                `End Date cannot be selected greater than today's date!`
            );
            return;
        default:
            const diff = endDateNum - startDateNum;
            switch (true) {
            case diff == 0:
                startValueSpy.reset(startValueSpy.value);
                endValueSpy.reset();
                this.toast.warn('Start Date and End Date cannot be the same!');
                return;
            case diff < 0:
                startValueSpy.reset(startValueSpy.value);
                endValueSpy.reset();
                this.toast.warn(
                    'End Date cannot be less than Start Date !!'
                );
                return;
            case diff / (24 * 60 * 60 * 1000) > 29:
                startValueSpy.reset(startValueSpy.value);
                endValueSpy.reset();
                this.toast.warn(
                    'Date range cannot be more than 30 days !!'
                );
                return;
            default:
                this.toast.clear();
                return true;
            }
        }
    }

    getAuditActions(): void {
        this.auditService
            .getAuditActions()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    this.auditActions = response ?? [];
                    this.setActionSelectOptions();
                },
            });
    }

    setActionSelectOptions(): void {
        this.actionSelect.options = this.auditActions?.map((action)=> ({displayKey: action, valueKey: action}));
        this.actionSelect.disabled = !this.auditActions?.length;
        this.actionSelect = {...this.actionSelect};
    }

    onSearch(value: string): void {
        this.searchSubject.next(value);
    }

    searchData(value: string): void {
        this.searchResults = [];
        const filterValue = {};
        const arr = [];
        for (const column of this.filterColumns) {
            const obj = {};
            obj[column] = value;
            arr?.push(obj);
        }
        filterValue['$or'] = [...arr];
        const tempArr = this.filterPipe?.transform(this.auditList, filterValue);
        this.searchResults = tempArr;
        this.initTooltip();
    }

    paginationHandler(value: any): void {
        this.paginationParams = value;
    }

    initTooltip(): void {
        this.commonService.initTooltip('com-audit-log');
    }
}
